import type { Collection, CollectionDefaults, ResolvedCollection, StoreSchema } from '@rstore/shared';
import type { CrudActionType as CrudActionName } from '#layers/access';
import type { PrimitivePrimaryKey } from '#layers/data-model';
import type { CustomCollectionMeta } from '#shared/types/rstore';
import { joinURL } from 'ufo';
import { useRuntimeConfig } from '#app/nuxt';
import { isNil } from '#layers/base';
import { hasCompositeKey } from './has-composite-key';

interface ApiEndpointOptions<TCollection extends Collection = Collection, TCollectionDefaults extends CollectionDefaults = CollectionDefaults, TSchema extends StoreSchema = StoreSchema> {
	collection: ResolvedCollection<TCollection, TCollectionDefaults, TSchema>;
	key?: PrimitivePrimaryKey;

	projectSlug?: string;

	operation: CrudActionName;
}

/**
 * Builds the API endpoint URL for a collection, optionally scoped to a project and/or specific item
 */
export function apiEndpoint<TCollection extends Collection = Collection, TCollectionDefaults extends CollectionDefaults = CollectionDefaults, TSchema extends StoreSchema = StoreSchema>(options: ApiEndpointOptions<TCollection, TCollectionDefaults, TSchema>) {
	const { collection, key, projectSlug, operation } = options;
	const segments: string[] = [];

	if (projectSlug) {
		segments.push(projectSlug);
	}

	const { endpoint } = collection.meta ?? {};

	const endpointForAction = extractEndpointFromConfig(endpoint, !isNil(key), operation);

	if (endpointForAction) {
		segments.push(endpointForAction);
	}
	else {
		segments.push(`items/${collection.name}`);
	}

	if (!collection.meta?.singleton && !isNil(key) && (typeof key !== 'string' || key.length > 0)) {
		if (hasCompositeKey(collection)) {
			// TODO depending on the engine syntax
			throw new Error('not-implemented: composite keys in apiEndpoint');
		}

		segments.push(String(key));
	}

	return joinURL(useRuntimeConfig().public.api, ...segments);
}

function extractEndpointFromConfig(endpointConfig: CustomCollectionMeta['endpoint'] | undefined, single: boolean, action: CrudActionName) {
	if (!endpointConfig) {
		// don't return anything
	}
	else if (typeof endpointConfig === 'string') {
		return endpointConfig;
	}
	else if (typeof endpointConfig === 'object') {
		const forCardinality = endpointConfig[single ? 'single' : 'many'];

		if (forCardinality) {
			if (typeof forCardinality === 'string') {
				// single endpoint for all actions
				return forCardinality;
			}
			else if (typeof forCardinality === 'object') {
				return forCardinality[action];
			}
		}
	}
}
